// eslint-disable-next-line import/no-cycle
import { adminApi } from '@/includes/contentManagementApi';

/**
 * Service for handling assessment-related API calls
 */
class AssessmentService {
  /**
   * Get all assessments
   * @returns {Promise} Promise containing the assessments list
   */
  static getAllAssessments() {
    return adminApi.get('/assessments');
  }

  /**
   * Get a specific assessment by ID
   * @param {string} id - The assessment ID
   * @returns {Promise} Promise containing the assessment data
   */
  static getAssessmentById(id) {
    return adminApi.get(`/assessments/${id}`);
  }

  /**
   * Create a new assessment
   * @param {Object} assessment - The assessment data to create
   * @returns {Promise} Promise containing the created assessment
   */
  static createAssessment(assessment) {
    return adminApi.post('/assessments', assessment);
  }

  /**
   * Update an existing assessment
   * @param {string} id - The assessment ID to update
   * @param {Object} assessment - The updated assessment data
   * @returns {Promise} Promise containing the updated assessment
   */
  static updateAssessment(id, assessment) {
    return adminApi.patch(`/assessments/${id}`, assessment);
  }

  /**
   * Delete an assessment
   * @param {string} id - The assessment ID to delete
   * @returns {Promise} Promise containing the deletion result
   */
  static deleteAssessment(id) {
    return adminApi.delete(`/assessments/${id}`);
  }

  /**
   * Get dimensions for a specific assessment
   * @param {string} id - The assessment ID
   * @returns {Promise} Promise containing the assessment dimensions
   */
  static getAssessmentDimensions(id) {
    return adminApi.get(`/assessments/${id}/dimensions`);
  }

  /**
   * Get charts for a specific assessment
   * @param {string} id - The assessment ID
   * @returns {Promise} Promise containing the assessment charts
   */
  static getAssessmentCharts(id) {
    return adminApi.get(`/assessments/${id}/charts`);
  }

  /**
   * Get linkable assessments based on dimensions
   * @param {string} id - The assessment ID
   * @param {Array<string>} dimensions - Array of dimension IDs
   * @returns {Promise} Promise containing the linkable assessments
   */
  static getLinkableAssessments(id, dimensions) {
    return adminApi.get(`/assessments/${id}/linkable`, {
      params: { dimensions },
    });
  }

  /**
   * Get assessment results for a specific user
   * @param {string} userId - The user ID
   * @param {Object} filter - Filter parameters for the results
   * @returns {Promise} Promise containing the user's assessment results
   */
  static getUserAssessmentsResults(filter) {
    return adminApi.get('/assessments/user', {
      params: filter,
    });
  }

  /**
   * Get assessment results for the My Results Dashboard
   * This endpoint explicitly excludes external evaluation assessments (e.g., mindset360)
   * which are fetched separately via getUserExternalEvaluationResults
   * @param {Object} filter - Filter parameters for the results
   * @returns {Promise} Promise containing the user's assessment results for dashboard
   */
  static getUserAssessmentsResultsForDashboard(filter) {
    return adminApi.get('/assessments/user/dashboard', {
      params: filter,
    });
  }

  /**
   * Get external evaluation results for the current user
   * Generalized method that works with any external evaluation type
   * Falls back to mindset360 endpoint for backward compatibility
   * @param {Object} filter - Filter parameters for the results
   * @param {string} filter.schemaType - Optional: specific external evaluation schema type
   * @returns {Promise} Promise containing the user's external evaluation results
   */
  static getUserExternalEvaluationResults(filter) {
    // Use the mindset360 endpoint which now handles all external evaluations via seriesId
    return adminApi.get('/assessments/user/externalEvaluation', {
      params: filter,
    });
  }

  /**
   * Get the last external evaluation date per schema type for the current user
   * Used for enforcing 6-month cooldown per schema type on the frontend
   * @returns {Promise} Promise containing { schemaType: Date } mapping
   */
  static getLastExternalEvaluationDates() {
    return adminApi.get('/assessments/externalEvaluation/lastDates');
  }

  static getUserTeamAssessmentsResults(data, config) {
    return adminApi.post('/assessments/user/team', data, config);
  }

  /**
   * Get assessments by user ID
   * @param {string} userId - The user ID
   * @returns {Promise} Promise containing the user's assessments
   */
  static getAssessmentsByUserId(userId) {
    return adminApi.get(`/assessments/admin/user/${userId}`);
  }

  /**
   * Get system assessment overview
   * @returns {Promise} Promise containing the system overview
   */
  static getSystemAssessmentOverview() {
    return adminApi.get('/assessments/admin/system-overview');
  }

  /**
   * Get assessments by status
   * @param {string} status - The assessment status
   * @returns {Promise} Promise containing assessments with the specified status
   */
  static getAssessmentsByStatus(status) {
    return adminApi.get(`/assessments/admin/by-status/${status}`);
  }

  /**
   * Get dirty assessments report
   * @returns {Promise} Promise containing the dirty assessments report
   */
  static getDirtyAssessmentsReport() {
    return adminApi.get('/assessments/admin/dirty-assessments-report');
  }

  /**
   * Get all assessments with pagination
   * @param {Object} params - Pagination and filter parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 10)
   * @param {string} params.status - Optional status filter
   * @param {string} params.filterType - Optional filter type (empty-questions, weekly-duplicates)
   * @param {string} params.userRoles - Optional assignee role filter (single role or comma-separated)
   * @returns {Promise} Promise containing paginated assessments
   */
  static getAllAssessmentsPaginated(params) {
    return adminApi.get('/assessments/admin/all', { params });
  }

  /**
   * Find duplicate assessments within a time window
   * @param {number} timeWindowHours - Number of hours to look back for duplicates
   * @returns {Promise} Promise containing duplicate assessments grouped by user+type
   */
  static findDuplicateAssessments(timeWindowHours = 24) {
    return adminApi.get('/assessments/admin/duplicates', {
      params: { timeWindowHours },
    });
  }

  /**
   * Get cleaning proposal for duplicate assessments
   * @param {Object|number} params - Either timeWindowHours (number) or options object
   * @param {number} params.timeWindowHours - Number of hours to look back for duplicates
   * @param {string|null} params.assessmentType - Optional assessment type filter
   * @returns {Promise} Promise containing cleaning proposal with actions
   */
  static proposeDuplicateCleaning(params = {}) {
    const queryParams =
      typeof params === 'number'
        ? { timeWindowHours: params }
        : {
          timeWindowHours: params.timeWindowHours || 24,
          ...(params.assessmentType && { assessmentType: params.assessmentType }),
        };
    return adminApi.get('/assessments/admin/duplicates/propose', {
      params: queryParams,
    });
  }

  /**
   * Execute cleaning by deleting proposed assessments
   * @param {Array<string>} assessmentIds - Array of assessment IDs to delete
   * @returns {Promise} Promise containing deletion results
   */
  static executeDuplicateCleaning(assessmentIds) {
    return adminApi.post('/assessments/admin/duplicates/clean', {
      assessmentIds,
    });
  }

  /**
   * Find assessments with undefined dimensions
   * @returns {Promise} Promise containing assessments with undefined dimensions
   */
  static findUndefinedDimensions() {
    return adminApi.get('/assessments/admin/undefined-dimensions');
  }

  /**
   * Assign dimensions to assessments with undefined dimensions
   * @param {Array<string>} assessmentIds - Array of assessment IDs to assign dimensions to
   * @returns {Promise} Promise containing assignment results
   */
  static assignDimensionsToUndefined(assessmentIds) {
    return adminApi.post('/assessments/admin/fix-undefined-dimensions', {
      assessmentIds,
    });
  }
}

export default AssessmentService;
